// cleanup_convert_to_glu_component_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS
// cleanup_remove_external_libraries_PAYMENT_MAPS

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import moment from 'moment';
import { get, template } from 'lodash';

import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';

import Alert from 'components/alert/Alert';
import ImportMappingTable from 'components/importMapping/ImportMappingTable';
import Spinner from 'components/spinner/Spinner';
import useLocale from 'components/useLocale';
import PlaceResolver, { variants } from 'components/placeResolver/PlaceResolver';
import { deleteWireImportMap, retrieveWireImportMap } from 'api/wireImports/service';
import { getDelimiters, getCharges, getEndOfRecordOptions } from 'api/payments/service';
import { DATE_FORMAT, WORKSPACES } from 'constants.js';

import importMapColumns, { messageRow } from './retrieveImportMapColumns';
import useStyles from './retrieveWireImportMap.styles';
import { headerDeterminedFor } from '../utils';

const RetrieveWireImportMapComponent = ({ closeModal, mapId }) => {
  const locale = useLocale();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const [apiData, set] = useState(null);
  const [error, setError] = useState(null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    const { paymentType } = location.state || {};

    retrieveWireImportMap({ action: 'View', mapId, paymentType }).subscribe(({ data }) => {
      if (get(data, '0.code')) {
        setFailure(locale['wireImports.mapErrors.default']);
        return;
      }
      set(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failure) {
    return failure;
  }

  if (!apiData) {
    return <Spinner />;
  }

  const tableData = get(apiData, 'wireImportMapDetail').filter((f) => f.isDisplay === 'Y');

  // sort by position
  const records = tableData.sort((a, b) => (a.order > b.order ? 1 : -1));

  const onDeleteMap = () => {
    deleteWireImportMap({ mapId: get(apiData, 'wireImportMapHeader.mtMapId') }).subscribe(
      ({ data, success }) => {
        // use response to populate mess
        if (!success) {
          const code = get(data, '0.code');
          const map = get(apiData, 'wireImportMapHeader.mapName');

          const fallback = locale['wireImports.mapErrors.default'];
          const messages = {
            WIRE_IMPORT_MAP_DOESNT_EXIST: template(
              locale['wireImports.mapErrors.WIRE_IMPORT_MAP_DOESNT_EXIST']
            )({ map }),
            CANNOT_DELETE_WIRE_MAP: template(
              locale['wireImports.mapErrors.CANNOT_DELETE_WIRE_MAP']
            )({ map }),
            CORBA_CALL_EXCEPTION: fallback
          };

          setError(messages[code] || fallback);
        } else {
          history.push({
            pathname: location?.state?.previous || WORKSPACES.PYMTCNTR,
            state: {
              messages: [
                {
                  id: 1,
                  title: locale['payments.manageImportMaps.delete.succeeded'],
                  data: { mapName: get(apiData, 'wireImportMapHeader.mapName') },
                  success: true
                }
              ]
            }
          });
        }
      }
    );
  };

  const {
    createdBy,
    delimiter,
    endofRecordMethod,
    lastUsedBy,
    lastUsedDate,
    mapName,
    mapType,
    possibleActions,
    skipHeaderRecord,
    stripQuotes,
    wireType
  } = get(apiData, 'wireImportMapHeader');

  const mappings = {
    lineEndings: {
      'ccr/lf': 'CR/LF',
      llf: 'LF'
    },
    yesno: {
      0: 'No',
      Y: 'Yes',
      N: 'No'
    }
  };
  const actions = {
    modify: possibleActions.includes('Modify'),
    delete: possibleActions.includes('Delete')
  };

  const fixed = mapType.toLowerCase() === 'fixed';

  return (
    <PlaceResolver
      services={[getDelimiters, getCharges, getEndOfRecordOptions]}
      variant={variants.table}
    >
      {({
        results: [
          { dropdown: delimiters },
          { dropdown: charges },
          { dropdown: endOfRecordOptions }
        ]
      }) => {
        const delimiterFound = delimiters.source.find((del) => del.code === delimiter);
        const endOFRecordFound = endOfRecordOptions.source.find(
          (e) => e.code === endofRecordMethod
        );
        const delimiterWord = delimiterFound && delimiterFound.name;
        const endOfRecordWord = endOFRecordFound && endOFRecordFound.name;
        const endOfRecordRecordLength =
          endofRecordMethod.indexOf('R') === 0 && `Record Length ${endofRecordMethod.slice(1)}`;

        return (
          <div className={classes.wrapper}>
            <h1 className={classes.title}>
              {template(locale['wireImports.viewImportMap'])({
                determinedFor: locale[`wireImports.${headerDeterminedFor[wireType]}`]
              })}
            </h1>
            {error && (
              <Alert className={classes.alert} onHide={() => setError(null)} variant="warning">
                <p className={classes.alertTitle}>{locale['wireImports.canNotDeleteMap']}</p>
                {error}
              </Alert>
            )}
            <section className={classes.topSection}>
              <h2 className={classes.sectionTitle}>{locale['wireImports.mapDetails']}</h2>
              <div className={classes.details}>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.name']}
                </div>
                <div className={classnames('col-md-6')}>{mapName || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.paymentType']}
                </div>
                <div className={classnames('col-md-6')}>{wireType || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.mapType']}
                </div>
                <div className={classnames('col-md-6')}>{mapType || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.createdBy']}
                </div>
                <div className={classnames('col-md-6')}>{createdBy || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.lastUsedDate']}
                </div>
                <div className={classnames('col-md-6')}>
                  {lastUsedDate ? moment(lastUsedDate).format(DATE_FORMAT) : '-'}
                </div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.lastUsedBy']}
                </div>
                <div className={classnames('col-md-6')}>{lastUsedBy || '-'}</div>
              </div>
            </section>
            <section className={classes.topSection} data-qa="map-parameters">
              <h2 className={classes.sectionTitle}>{locale['wireImports.mapParameters']}</h2>
              <div className={classes.details}>
                {!fixed && (
                  <>
                    <div className={classnames(classes.label, 'col-md-6')} data-qa="delimiter">
                      {locale['payments.manageImportMaps.delimiter']}
                    </div>
                    <div className={classnames('col-md-6')} data-qa="delimiter-value">
                      {delimiterWord || '-'}
                    </div>
                  </>
                )}
                {!fixed && (
                  <>
                    <div className={classnames(classes.label, 'col-md-6')}>
                      {locale['payments.manageImportMaps.stripQuotes']}
                    </div>
                    <div className={classnames('col-md-6')}>
                      {mappings.yesno[stripQuotes] || '-'}
                    </div>
                  </>
                )}
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.skipFirstRecord']}
                </div>
                <div className={classnames('col-md-6')}>
                  {mappings.yesno[skipHeaderRecord] || skipHeaderRecord}
                </div>
                <div className={classnames(classes.label, 'col-md-6')} data-qa="end-of-record">
                  {locale['payments.manageImportMaps.endOfRecordMethod']}
                </div>
                <div className={classnames('col-md-6')} data-qa="end-of-record-value">
                  {endOfRecordWord || endOfRecordRecordLength || '-'}
                </div>
              </div>
            </section>
            <ImportMappingTable
              classes={classes}
              columns={importMapColumns({ locale, data: { charges, fixed } })}
              data={records}
              messageRow={messageRow(locale, fixed)}
              name="wire_map_table"
              rowKey="fieldPosition"
            />
            <div className={classes.buttons}>
              {actions.delete && (
                <Button className="btn btn-primary" onClick={onDeleteMap} variant={PRIMARY}>
                  {locale['common.delete']}
                </Button>
              )}

              {actions.modify && (
                <Button
                  className="btn btn-primary"
                  onClick={() =>
                    history.push({
                      pathname: `/payments/managePaymentMap/modify/wires/${mapId}`,
                      state: {
                        paymentType: wireType,
                        previous: location?.state?.previous || WORKSPACES.PYMTCNTR
                      }
                    })
                  }
                  variant={PRIMARY}
                >
                  {locale['common.modify']}
                </Button>
              )}
              <Button
                className="btn btn-tertiary"
                onClick={() => closeModal()}
                variant={SECONDARY}
              >
                {locale['common.close']}
              </Button>
            </div>
          </div>
        );
      }}
    </PlaceResolver>
  );
};

RetrieveWireImportMapComponent.propTypes = {
  mapId: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired
};

export default RetrieveWireImportMapComponent;
